import { MRT_TableInstance } from 'material-react-table'
import { useCallback, useEffect } from 'react'

type UseMrtKeyboardNavigationProps<TData extends Record<string, unknown>> = {
	containerRef?: React.RefObject<HTMLElement>,
	deleteRow?: (rowId: number) => void,
	editMode: boolean,
	onInsertRow?: (rowIndex: number) => void,
	onSave?: () => void,
	onTabChange?: (direction: 'next' | 'prev') => void
	table: MRT_TableInstance<TData>
}

/**
 * Keyboard navigation hook for Material React Table
 * Supports both Windows/Linux (Ctrl) and Mac (⌘ Cmd) shortcuts
 *
 * DataGrid Navigation (Columns, Sources, Permissions):
 * - Arrow keys (↑↓←→): Navigate between cells
 * - Ctrl/⌘ + ↑/↓: Jump to first/last cell in column
 * - Ctrl/⌘ + Enter: Insert new row below current
 * - Ctrl/⌘ + Delete (or ⌘ + Backspace on Mac): Delete current row
 * - Tab / Shift+Tab: Move between cells and grid areas
 *
 * Edit Mode:
 * - F2 / Enter: Start editing cell
 * - Esc: Cancel editing
 * - Ctrl/⌘ + S: Save changes
 *
 * Page Navigation:
 * - Ctrl/⌘ + PgUp/PgDn: Switch tabs
 * - Ctrl/⌘ + ↓: Jump to editors below table
 * - Alt/⌥ + ↑: Focus toolbar
 * - Alt/⌥ + ←: Focus breadcrumbs
 *
 * Multiline Editors:
 * - F11: Toggle fullscreen editor
 * - Enter: New line (in multiline)
 * - Ctrl/⌘ + Tab: Insert tab character
 */
export const useMrtKeyboardNavigation = <TData extends Record<string, unknown>>({
	table,
	editMode,
	onInsertRow,
	deleteRow,
	containerRef,
	onTabChange,
	onSave,
}: UseMrtKeyboardNavigationProps<TData>) => {
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			// Only handle if we're in the table container or it's a global shortcut
			const isInContainer =
				!containerRef?.current ||
				containerRef.current.contains(event.target as Node)

			const { key, ctrlKey, metaKey, shiftKey, altKey } = event
			
			// Support both Ctrl (Windows/Linux) and Cmd (Mac)
			const modKey = ctrlKey || metaKey

			// Get current focused element
			const activeElement = document.activeElement as HTMLElement
			const isInInput =
				activeElement?.tagName === 'INPUT' ||
				activeElement?.tagName === 'TEXTAREA' ||
				activeElement?.isContentEditable ||
				activeElement?.closest('.monaco-editor')

			// === GLOBAL SHORTCUTS (work even when not in container) ===

			// Ctrl/Cmd + PgUp/PgDn: Switch tabs
			if (modKey && (key === 'PageUp' || key === 'PageDown')) {
				event.preventDefault()
				onTabChange?.(key === 'PageUp' ? 'prev' : 'next')
				return
			}

			// Ctrl/Cmd + S: Save (only when not in an input - let inputs handle their own save)
			if (modKey && key === 's') {
				const activeEl = document.activeElement as HTMLElement
				const isInInputField =
					activeEl?.tagName === 'INPUT' ||
					activeEl?.tagName === 'TEXTAREA' ||
					activeEl?.isContentEditable ||
					activeEl?.closest('.monaco-editor')
				
				// If in an input, don't handle here - let the input component handle it
				if (isInInputField) {
					return
				}
				
				event.preventDefault()
				onSave?.()
				return
			}

			// Only handle container-specific shortcuts if in container
			if (!isInContainer) return

			// === EDIT MODE SHORTCUTS ===

			// Ctrl/Cmd + Enter: Insert new row
			if (modKey && key === 'Enter' && editMode && onInsertRow) {
				event.preventDefault()
				const rows = table.getRowModel().rows
				
				// Find current row by checking various MRT row attributes
				const tableRow = activeElement?.closest('tr[data-index]') as HTMLElement
				const rowIndex = tableRow ? parseInt(tableRow.dataset.index ?? '-1', 10) : -1
				
				if (rowIndex >= 0) {
					onInsertRow(rowIndex)
				} else if (rows.length > 0) {
					// Insert at end if no row is focused
					onInsertRow(rows.length - 1)
				} else {
					// Insert first row if table is empty
					onInsertRow(-1)
				}
				return
			}

			// Ctrl/Cmd + Delete (or Cmd + Backspace on Mac): Delete current row
			if (modKey && (key === 'Delete' || key === 'Backspace') && editMode && deleteRow) {
				event.preventDefault()
				const rows = table.getRowModel().rows
				
				// Find current row by checking MRT row attributes
				const tableRow = activeElement?.closest('tr[data-index]') as HTMLElement
				const rowIndex = tableRow ? parseInt(tableRow.dataset.index ?? '-1', 10) : -1
				
				if (rowIndex >= 0 && rows[rowIndex]) {
					deleteRow(rows[rowIndex].original.id as number)
				}
				return
			}

			// If editing a cell, handle editor-specific shortcuts
			if (isInInput) {
				// Esc: Cancel editing
				if (key === 'Escape') {
					activeElement.blur()
					return
				}

				// Arrow keys without modifier: let them work normally within the editor
				if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key) && !modKey) {
					// Don't prevent default - let cursor move within the editor
					return
				}

				// Ctrl/Cmd + Arrows: Save and move to adjacent cell
				// Skip when Shift is held: browser uses Cmd/Ctrl+Shift+Arrow for text selection
				if (modKey && !shiftKey && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
					event.preventDefault()
					// Blur to save, then navigate
					activeElement.blur()
					// Small delay to allow blur to complete
					setTimeout(() => {
						navigateToCell(key as 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight')
					}, 10)
					return
				}

				// Tab: Move to next cell (let default behavior work)
				// Ctrl/Cmd + Tab: Insert tab character (browser handles this)
				return
			}

			// === NAVIGATION MODE SHORTCUTS (not in input) ===

			// F2 or Enter: Start editing
			if ((key === 'F2' || key === 'Enter') && editMode) {
				event.preventDefault()
				const cell = activeElement?.closest('td')
				const input = cell?.querySelector(
					'input, textarea, [contenteditable="true"]',
				) as HTMLElement
				if (input) {
					input.focus()
					if (input.tagName === 'INPUT') {
						;(input as HTMLInputElement).select()
					}
				} else {
					// For expandable cells (ExpandableTextCell, MonacoScriptCell), 
					// find the focusable box and trigger Enter on it
					const focusableBox = cell?.querySelector('[tabindex="0"]') as HTMLElement
					if (focusableBox) {
						focusableBox.focus()
						// Dispatch Enter key event to trigger the cell's onKeyDown handler
						focusableBox.dispatchEvent(new KeyboardEvent('keydown', { 
							key: 'Enter', 
							bubbles: true, 
							cancelable: true 
						}))
					}
				}
				return
			}

			// Arrow key navigation
			if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
				event.preventDefault()

				// Ctrl/Cmd + Arrow: Jump to edge
				if (modKey) {
					navigateToEdge(key as 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight')
				} else {
					navigateToCell(key as 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight')
				}
				return
			}

			// Alt/Option + Up: Focus toolbar
			if (altKey && key === 'ArrowUp') {
				event.preventDefault()
				const toolbar = document.querySelector('[data-testid="table-toolbar"], [data-testid="detail-tab-header"]') as HTMLElement
				toolbar?.focus()
				return
			}

			// Alt/Option + Left: Focus breadcrumbs
			if (altKey && key === 'ArrowLeft') {
				event.preventDefault()
				const breadcrumbs = document.querySelector('[data-testid="breadcrumbs"], .MuiBreadcrumbs-root') as HTMLElement
				const firstLink = breadcrumbs?.querySelector('a, button') as HTMLElement
				firstLink?.focus()
				return
			}

			// Ctrl/Cmd + Down: Jump to editors below table
			if (modKey && key === 'ArrowDown') {
				event.preventDefault()
				const nextEditor = containerRef?.current
					?.closest('.MuiBox-root')
					?.querySelector('.ace_editor, .monaco-editor, textarea') as HTMLElement
				nextEditor?.focus()
				return
			}
		},
		[containerRef, deleteRow, editMode, onInsertRow, onSave, onTabChange, table],
	)

	// Helper: Navigate to adjacent cell using table structure
	const navigateToCell = useCallback(
		(direction: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight') => {
			const container = containerRef?.current
			if (!container) return

			const activeElement = document.activeElement as HTMLElement
			
			// Find current cell (td element)
			const currentCell = activeElement.closest('td') as HTMLTableCellElement
			if (!currentCell) return

			// Find current row
			const currentRow = currentCell.closest('tr') as HTMLTableRowElement
			if (!currentRow) return

			// Get all rows in the table body
			const tableBody = container.querySelector('tbody')
			if (!tableBody) return

			const rows = Array.from(tableBody.querySelectorAll('tr[data-index]')) as HTMLTableRowElement[]
			const currentRowIndex = rows.indexOf(currentRow)
			
			// Get all cells in current row (excluding action/drag columns)
			const getCellsInRow = (row: HTMLTableRowElement) => {
				return Array.from(row.querySelectorAll('td')).filter(cell => {
					// Skip action columns and drag handle columns
					const isActionColumn = cell.querySelector('[data-testid*="action"]') || 
						cell.classList.contains('MuiTableCell-alignRight')
					return !isActionColumn
				}) as HTMLTableCellElement[]
			}

			const cellsInCurrentRow = getCellsInRow(currentRow)
			const currentCellIndex = cellsInCurrentRow.indexOf(currentCell)

			let targetCell: HTMLTableCellElement | null = null

			switch (direction) {
				case 'ArrowRight':
					// Move to next cell in same row
					if (currentCellIndex < cellsInCurrentRow.length - 1) {
						targetCell = cellsInCurrentRow[currentCellIndex + 1]
					}
					break
				case 'ArrowLeft':
					// Move to previous cell in same row
					if (currentCellIndex > 0) {
						targetCell = cellsInCurrentRow[currentCellIndex - 1]
					}
					break
				case 'ArrowDown':
					// Move to same column in next row
					if (currentRowIndex < rows.length - 1) {
						const nextRow = rows[currentRowIndex + 1]
						const cellsInNextRow = getCellsInRow(nextRow)
						targetCell = cellsInNextRow[Math.min(currentCellIndex, cellsInNextRow.length - 1)]
					}
					break
				case 'ArrowUp':
					// Move to same column in previous row
					if (currentRowIndex > 0) {
						const prevRow = rows[currentRowIndex - 1]
						const cellsInPrevRow = getCellsInRow(prevRow)
						targetCell = cellsInPrevRow[Math.min(currentCellIndex, cellsInPrevRow.length - 1)]
					}
					break
			}

			if (targetCell) {
				focusCell(targetCell)
			}
		},
		[containerRef],
	)

	// Helper: Navigate to edge cell (first/last in row or column)
	const navigateToEdge = useCallback(
		(direction: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight') => {
			const container = containerRef?.current
			if (!container) return

			const activeElement = document.activeElement as HTMLElement
			const currentCell = activeElement.closest('td') as HTMLTableCellElement
			if (!currentCell) return

			const currentRow = currentCell.closest('tr') as HTMLTableRowElement
			if (!currentRow) return

			const tableBody = container.querySelector('tbody')
			if (!tableBody) return

			const rows = Array.from(tableBody.querySelectorAll('tr[data-index]')) as HTMLTableRowElement[]
			
			const getCellsInRow = (row: HTMLTableRowElement) => {
				return Array.from(row.querySelectorAll('td')).filter(cell => {
					const isActionColumn = cell.querySelector('[data-testid*="action"]')
					return !isActionColumn
				}) as HTMLTableCellElement[]
			}

			const cellsInCurrentRow = getCellsInRow(currentRow)
			const currentCellIndex = cellsInCurrentRow.indexOf(currentCell)

			let targetCell: HTMLTableCellElement | null = null

			switch (direction) {
				case 'ArrowRight':
					// Jump to last cell in row
					targetCell = cellsInCurrentRow[cellsInCurrentRow.length - 1]
					break
				case 'ArrowLeft':
					// Jump to first cell in row
					targetCell = cellsInCurrentRow[0]
					break
				case 'ArrowDown':
					// Jump to same column in last row
					if (rows.length > 0) {
						const lastRow = rows[rows.length - 1]
						const cellsInLastRow = getCellsInRow(lastRow)
						targetCell = cellsInLastRow[Math.min(currentCellIndex, cellsInLastRow.length - 1)]
					}
					break
				case 'ArrowUp':
					// Jump to same column in first row
					if (rows.length > 0) {
						const firstRow = rows[0]
						const cellsInFirstRow = getCellsInRow(firstRow)
						targetCell = cellsInFirstRow[Math.min(currentCellIndex, cellsInFirstRow.length - 1)]
					}
					break
			}

			if (targetCell) {
				focusCell(targetCell)
			}
		},
		[containerRef],
	)

	// Helper: Focus a cell and its input
	const focusCell = (cell: HTMLElement | undefined) => {
		if (!cell) return
		
		// Try to find and focus an input element within the cell
		const input = cell.querySelector(
			'input:not([type="hidden"]), textarea, [contenteditable="true"], .monaco-editor textarea'
		) as HTMLElement
		
		if (input) {
			input.focus()
			// Select text in input fields for easy overwriting
			if (input.tagName === 'INPUT' && (input as HTMLInputElement).type === 'text') {
				(input as HTMLInputElement).select()
			}
		} else {
			// Focus the cell itself if no input found
			cell.setAttribute('tabindex', '-1')
			cell.focus()
		}
	}

	useEffect(() => {
		const container = containerRef?.current || document
		container.addEventListener('keydown', handleKeyDown as EventListener)
		return () => {
			container.removeEventListener('keydown', handleKeyDown as EventListener)
		}
	}, [containerRef, handleKeyDown])

	return { handleKeyDown }
}
